import React from 'react';
import { NumericFormat } from 'react-number-format';

import { Input, InputProps } from '@/shared/ui/Input/Input';
import { cn } from '@/shared/lib/tailwind-merge';

type NumericInputProps = {
    value: string;
    onRawValueChange: (raw: string) => void;
    decimals: number;
    disabled?: boolean;
    name?: string;
} & Partial<Pick<InputProps, 'leftIcon' | 'rightIcon' | 'size' | 'color' | 'className'>>;

const isScientific = (val?: string): boolean =>
    typeof val === 'string' && val.toLowerCase().includes('e');

const normalize = (val?: string): string => {
    if (!val) return '';
    return isScientific(val)
        ? Number(val)
              .toFixed(30)
              .replace(/\.?0+$/, '')
        : val;
};

const preventScientificKey = (e: React.KeyboardEvent<HTMLInputElement>, currentValue: string) =>
    ['e', 'E'].includes(e.key) ||
    (e.key === '.' && currentValue.includes('.') && e.preventDefault());

const preventScientificPaste = (e: React.ClipboardEvent<HTMLInputElement>) =>
    isScientific(e.clipboardData.getData('Text')) && e.preventDefault();

const allowOnlyOneDot = (formattedValue: string) =>
    (formattedValue.match(/\./g) || []).length <= 1 && !isScientific(formattedValue);

export const NumericInput: React.FC<NumericInputProps> = ({
    value,
    onRawValueChange,
    decimals,
    disabled = false,
    name,
    leftIcon,
    rightIcon,
    size,
    color,
    className,
}) => (
    <NumericFormat
        value={normalize(value)}
        thousandSeparator
        allowNegative={false}
        decimalScale={decimals}
        disabled={disabled}
        placeholder='0'
        name={name}
        onKeyDown={(e) => preventScientificKey(e, value)}
        onPaste={preventScientificPaste}
        isAllowed={({ formattedValue }) => allowOnlyOneDot(formattedValue)}
        onValueChange={({ value: raw }) => onRawValueChange(normalize(raw ?? ''))}
        customInput={Input}
        className={cn('!text-[20px] !font-semibold !text-content-primary !border-none', className)}
        inputMode='decimal'
        size={size}
        color={color}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
    />
);
